import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
    Button,
    Box,
    VStack,
    HStack,
    Text,
    Badge,
    Link,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useToast,
    Spinner,
    Alert,
    AlertIcon
} from '@chakra-ui/react'
import * as React from 'react'
import { Campaign, CampaignEvent } from 'graphql/types'
import getCampaignEvents from 'graphql/queries/getCampaignEvents'
import campaignDraftEvent from 'graphql/queries/campaignDraftEvent'
import sendTestCampaignEmail from 'graphql/queries/sendTestCampaignEmail'
import campaign from 'graphql/mutations/campaign'
import { EditCopy } from './edit'
import { Clicks } from './clicks'
import { DraftDetails } from './draft'
import { LogicBuilder } from './LogicBuilder'

type Props = {
    Campaign: Campaign
    refetchCampaigns: () => void
    setEditCampaign: (Campaign: Campaign | null) => void
}

const CampaignDetails = ({Campaign, refetchCampaigns, setEditCampaign}: Props) => {
    const toast = useToast()

    const { loading, error, data, refetch } = useQuery(getCampaignEvents, {
        variables: {
            CampaignID: Campaign.ID
        }
    })

    const [testEmail] = useMutation(sendTestCampaignEmail, {
        variables: {
            CampaignID: Campaign.ID
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true
            })
        },
        onCompleted: () => {
            toast({
                title: 'Test email sent',
                status: 'success',
                duration: 3000,
                isClosable: true
            })
        }
    })

    const [mutateCampaign] = useMutation(campaign, {
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true
            })
        },
        onCompleted: () => {
            toast({
                title: 'Campaign updated',
                status: 'success',
                duration: 3000,
                isClosable: true
            })
        }
    })

    const [runGenerateDraft, draftResult] = useLazyQuery(campaignDraftEvent)
    const [draftLoading, setDraftLoading] = React.useState<boolean>(false)
    const [openModal, setOpenModal] = React.useState<string[] | null>(null)
    const [openEditModal, setOpenEditModal] = React.useState<Campaign | null>(null)
    const [openLogicBuilder, setOpenLogicBuilder] = React.useState<boolean>(false)

    const generateDraft = async (id: string) => {
        setDraftLoading(true)
        await runGenerateDraft({
            variables: {
                CampaignID: id
            }
        })
        setDraftLoading(false)
    }

    const processCampaign = async () => {
        await mutateCampaign({
            variables: {
                CampaignID: Campaign.ID,
                Status: 'SendOneOffDraft'
            }
        })
        refetch()
        setOpenModal(null)
    }

    const testCampaign = async () => {
        await mutateCampaign({
            variables: {
                CampaignID: Campaign.ID,
                Status: 'SendOneOffDraftTest'
            }
        })
        refetch()
        setOpenModal(null)
    }

    const updateStatus = async (campaignID: string, status: string) => {
        await mutateCampaign({
            variables: {
                CampaignID: campaignID,
                Status: status
            }
        })
        refetchCampaigns()
    }

    const updateCopy = async (
        campaignID: string,
        copy: string,
        subject: string,
        values: Array<{ key: string; value: string }>
    ) => {
        const updateValues = JSON.stringify(values)
        await mutateCampaign({
            variables: {
                CampaignID: campaignID,
                Copy: copy,
                Subject: subject,
                Values: updateValues
            }
        })
        refetch()
        setOpenEditModal(null)
    }

    const updateLogic = async (logic: string) => {
        await mutateCampaign({
            variables: {
                CampaignID: Campaign.ID,
                Logic: logic
            }
        })
        refetch()
        refetchCampaigns()
        setOpenLogicBuilder(false)
    }

    if (loading) {
        return (
            <Box p={4} textAlign="center">
                <Spinner />
                <Text mt={2}>Loading campaign details...</Text>
            </Box>
        )
    }

    if (error) {
        return (
            <Alert status="error">
                <AlertIcon />
                Error loading campaign: {error.message}
            </Alert>
        )
    }

    if (!data?.getCampaignEvents) {
        return (
            <Alert status="warning">
                <AlertIcon />
                No campaign events found
            </Alert>
        )
    }

    let events: CampaignEvent[] = data.getCampaignEvents

    if (draftResult.error) {
        events = [
            {
                AudienceTotal: '0',
                CampaignID: Campaign.ID,
                DateCreated: 'ERROR CREATING DRAFT: ' + draftResult.error.message,
                ID: '0',
                IsDraft: 'true',
                PushTotal: '0',
                Type: 'Draft'
            },
            ...events
        ]
    } else if (draftResult.data) {
        events = [draftResult.data.campaignDraftEvent, ...events]
    }

    const processedEvents = events
        .filter((a) => a.DateCreated)
        .sort((a, b) => Date.parse(b.DateCreated) - Date.parse(a.DateCreated))
        .slice(0, 10)
        .map((a) => ({
            CampaignID: a.CampaignID,
            Type: a.Type,
            ID: a.ID,
            DateCreated: a.DateCreated.includes('ERROR')
                ? a.DateCreated
                : new Date(a.DateCreated).toLocaleDateString(),
            DateUpdated: a.DateUpdated,
            AudienceTotal: a.AudienceTotal,
            PushedUserIDs: a.PushedUserIDs,
            PushTotal: a.PushTotal,
            IsDraft: a.IsDraft,
            Status: a.IsDraft === 'true' ? 'Draft' : 'Completed'
        }))

    const isActive = Campaign.Status === 'active'

    return (
        <Box p={4}>
            <VStack spacing={4} align="stretch">
                <Box
                    p={4}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                >
                    <HStack justify="space-between" mb={4} flexWrap="wrap">
                        <VStack align="start" spacing={1}>
                            <Text fontSize="2xl" fontWeight="bold">
                                {Campaign.Name}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                                Created: {Campaign.DateCreated}
                            </Text>
                            {Campaign.Description && (
                                <Text fontSize="md">{Campaign.Description}</Text>
                            )}
                            {Campaign.Doc && (
                                <Link href={Campaign.Doc} isExternal color="blue.500">
                                    View Documentation
                                </Link>
                            )}
                        </VStack>

                        <VStack spacing={2} align="end">
                            <Badge
                                colorScheme={isActive ? 'green' : 'red'}
                                fontSize="lg"
                                px={4}
                                py={2}
                                cursor="pointer"
                                onClick={() =>
                                    updateStatus(
                                        Campaign.ID,
                                        isActive ? 'inactive' : 'active'
                                    )
                                }
                            >
                                {Campaign.Status}
                            </Badge>
                            <Button
                                size="sm"
                                onClick={() => generateDraft(Campaign.ID)}
                                isLoading={draftLoading}
                                loadingText="Generating..."
                            >
                                Generate Draft
                            </Button>
                            <Button size="sm" onClick={() => testEmail()}>
                                Test Email
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => setOpenEditModal(Campaign)}
                            >
                                Edit Copy
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="purple"
                                onClick={() => setOpenLogicBuilder(true)}
                            >
                                Logic
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="blue"
                                onClick={() => setEditCampaign(Campaign)}
                            >
                                Edit Campaign
                            </Button>
                        </VStack>
                    </HStack>

                    {Campaign.Logic && (
                        <Box>
                            <Text fontWeight="bold" mb={2}>
                                Logic:
                            </Text>
                            <Box
                                whiteSpace="pre-wrap"
                                p={3}
                                bg="white"
                                border="1px solid"
                                borderColor="gray.300"
                                borderRadius="md"
                                fontFamily="monospace"
                                fontSize="sm"
                                maxW="50%"
                            >
                                {Campaign.Logic}
                            </Box>
                        </Box>
                    )}
                </Box>

                <Clicks Campaign={Campaign} />

                {processedEvents.length > 0 && (
                    <Box>
                        <Text fontSize="lg" fontWeight="bold" mb={2}>
                            Events
                        </Text>
                        <Table variant="simple" size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Date Created</Th>
                                    <Th>Audience Total</Th>
                                    <Th>Push Total</Th>
                                    <Th>Status</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {processedEvents.map((event, index) => (
                                    <Tr
                                        key={event.ID || index}
                                        cursor="pointer"
                                        _hover={{ bg: 'gray.50' }}
                                        onClick={() =>
                                            setOpenModal(event.PushedUserIDs || [])
                                        }
                                    >
                                        <Td>{event.DateCreated}</Td>
                                        <Td>{event.AudienceTotal}</Td>
                                        <Td>{event.PushTotal}</Td>
                                        <Td>
                                            <Badge
                                                colorScheme={
                                                    event.Status === 'Draft'
                                                        ? 'yellow'
                                                        : 'green'
                                                }
                                            >
                                                {event.Status}
                                            </Badge>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                )}

                <Modal isOpen={!!openModal} onClose={() => setOpenModal(null)}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Draft Details</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={6}>
                            <DraftDetails
                                userIDs={openModal || []}
                                processCampaign={processCampaign}
                                testCampaign={testCampaign}
                            />
                        </ModalBody>
                    </ModalContent>
                </Modal>

                <Modal
                    isOpen={openEditModal !== null}
                    onClose={() => setOpenEditModal(null)}
                    size="xl"
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Edit Copy</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={6}>
                            {openEditModal && (
                                <EditCopy
                                    handleSubmit={updateCopy}
                                    Campaign={openEditModal}
                                />
                            )}
                        </ModalBody>
                    </ModalContent>
                </Modal>

                <Modal
                    isOpen={openLogicBuilder}
                    onClose={() => setOpenLogicBuilder(false)}
                    size="4xl"
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Campaign Logic Builder</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={6}>
                            <LogicBuilder
                                initialLogic={Campaign.Logic || ''}
                                onSave={updateLogic}
                                onCancel={() => setOpenLogicBuilder(false)}
                            />
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </VStack>
        </Box>
    )
}

export default CampaignDetails
